import {
  Box,
  Button,
  Icon,
  OsStack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@outschool/backpack";
import {
  GetMeetingRecapQuery,
  GetMeetingRecapQueryVariables,
  UpdateMeetingRecapMutation,
  UpdateMeetingRecapMutationVariables,
} from "@outschool/gql-frontend-generated";
import { faEdit, faSparkles, faTrash, IconDefinition } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useMutation, useQueryWithPreviousData } from "@outschool/ui-apollo";
import React, { useCallback, useMemo, useState } from "react";

import {
  meetingRecapQuery,
  updateMeetingRecapMutation,
} from "../../ClassRecapPreview";

const MAX_SUMMARY_LENGTH = 10_000;

enum TAB_VALUES {
  SUMMARY = "summary",
}

type TabType = {
  icon: IconDefinition;
  label: string;
  value: TAB_VALUES;
};

interface ClassRecordingTabsProps {
  meetingUid: string;
  hasAiSummary: boolean;
  canManage?: boolean;
}

export function ClassRecordingTabs({
  meetingUid,
  hasAiSummary,
  canManage,
}: ClassRecordingTabsProps) {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\classRecording\\ClassRecordingTabs",
  );

  // Only one "Summary" tab for now, planning to add more in future versions
  const activeTab = TAB_VALUES.SUMMARY;
  const tabs: TabType[] = [
    {
      icon: faSparkles,
      label: t("Summary"),
      value: TAB_VALUES.SUMMARY,
    },
  ];

  const { data, loading } = useQueryWithPreviousData<
    GetMeetingRecapQuery,
    GetMeetingRecapQueryVariables
  >(meetingRecapQuery, {
    variables: { meetingUid },
    fetchPolicy: "cache-and-network",
  });

  const summaryText =
    data?.meetingAiSummary?.edited_summary_text ||
    data?.meetingAiSummary?.ai_summary_text;

  const TabContent = useMemo(() => {
    switch (activeTab) {
      case TAB_VALUES.SUMMARY:
        return summaryText ? (
          <SummaryTabContent
            summaryText={summaryText}
            meetingUid={meetingUid}
            canManage={canManage}
          />
        ) : null;
    }
  }, [activeTab, summaryText, canManage, meetingUid]);

  // If we are still loading the data or if the meeting has no AI summary to show, return null
  if ((loading && !data) || !hasAiSummary) {
    return null;
  }

  return (
    <OsStack direction="column" spacing={6}>
      <Tabs value={activeTab} sx={{ height: "60px" }}>
        {tabs.map(({ value, icon, label }) => (
          <Tab
            key={value}
            value={value}
            icon={
              <Icon icon={icon} sx={{ width: "28px", color: "warning.500" }} />
            }
            iconPosition="start"
            label={<Typography variant="h6">{label}</Typography>}
          />
        ))}
      </Tabs>
      {TabContent}
    </OsStack>
  );
}

interface SummaryTabContentProps {
  summaryText: string;
  meetingUid: string;
  canManage?: boolean;
}

function SummaryTabContent({
  summaryText,
  meetingUid,
  canManage,
}: SummaryTabContentProps) {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\classRecording\\ClassRecordingTabs",
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const trackEvent = useTrackEvent();

  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(summaryText);
  const [isUpdating, setIsUpdating] = useState(false);

  const [mutate] = useMutation<
    UpdateMeetingRecapMutation,
    UpdateMeetingRecapMutationVariables
  >(updateMeetingRecapMutation, {
    refetchQueries: [
      {
        query: meetingRecapQuery,
        variables: { meetingUid },
      },
    ],
  });

  const handleEditClick = useCallback(() => {
    setEditedText(summaryText);
    setIsEditing(true);
  }, [summaryText]);

  const handleCancelEdit = useCallback(() => {
    setEditedText(summaryText);
    setIsEditing(false);
  }, [summaryText]);

  const handleSaveEdit = useCallback(async () => {
    trackEvent("class_recording_ai_meeting_summary_edit_submit", {
      summaryText: summaryText,
      editedSummaryText: editedText,
    });
    setIsUpdating(true);
    const resp = await mutate({
      variables: {
        meetingUid,
        input: {
          edited_summary_text: editedText,
        },
      },
    });
    if (resp.errors) {
      OsPlatform.captureError(resp.errors);
    }
    setIsUpdating(false);
    setIsEditing(false);
  }, [mutate, meetingUid, summaryText, editedText, trackEvent]);

  const handleRemoveClick = useCallback(() => {
    trackEvent("class_recording_ai_meeting_summary_remove_click", {
      summaryText: summaryText,
    });
    mutate({
      variables: {
        meetingUid,
        input: {
          is_skipped: true,
          deleted_at: new Date(),
        },
      },
    });
  }, [mutate, meetingUid, summaryText, trackEvent]);

  return (
    <OsStack direction="column" spacing={6}>
      <Box
        sx={{
          backgroundColor: "primary.50",
          borderRadius: "16px",
          padding: "16px",
        }}
      >
        {isEditing ? (
          <OsStack direction="column" spacing={4}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={15}
              value={editedText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditedText(e.target.value)
              }
              inputProps={{ maxLength: MAX_SUMMARY_LENGTH }}
              disabled={isUpdating}
            />
            <OsStack
              direction="row"
              spacing={2}
              justifyContent={isMobile ? "space-between" : "flex-end"}
            >
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                disabled={isUpdating}
                fullWidth={isMobile}
              >
                {t("Cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={isUpdating || !editedText.trim()}
                fullWidth={isMobile}
              >
                {t("Update")}
              </Button>
            </OsStack>
          </OsStack>
        ) : (
          <OsStack direction={"column"} spacing={2}>
            <Typography
              variant="body1"
              sx={{ whiteSpace: "pre-line", flex: 1 }}
            >
              {summaryText}
            </Typography>
            {canManage && (
              <OsStack direction="row" spacing={2} alignItems="flex-start">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon icon={faEdit} />}
                  onClick={handleEditClick}
                >
                  {t("Edit")}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<Icon icon={faTrash} />}
                  onClick={handleRemoveClick}
                >
                  {t("Remove")}
                </Button>
              </OsStack>
            )}
          </OsStack>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: "neutral.600",
          paddingLeft: "16px",
        }}
      >
        {t`Summary generated by AI. It can make mistakes or miss context.`}
      </Typography>
    </OsStack>
  );
}
